import { SCHOOL_LEVELS, schoolMenus, type SchoolDetails, type SchoolLevelId } from "@rutamagica/contracts";

const toggle = (list: string[], value: string, on: boolean) =>
  on ? [...new Set([...list, value])] : list.filter((item) => item !== value);

/** Academic level, grades and brochure workshops of a school visit, all as checkboxes. */
export function SchoolPicker({ value, onChange }: { value: SchoolDetails; onChange: (next: SchoolDetails) => void }) {
  const setLevel = (id: SchoolLevelId, on: boolean) =>
    onChange({
      ...value,
      // Keep the order of the brochure catalog.
      levels: SCHOOL_LEVELS.map((level) => level.id).filter((level) => (level === id ? on : value.levels.includes(level))),
    });
  const setList = (key: "grades" | "workshops", level: SchoolLevelId, item: string, on: boolean) =>
    onChange({ ...value, [key]: { ...value[key], [level]: toggle(value[key][level] ?? [], item, on) } });
  return (
    <section className="school-picker" aria-label="Nivel, grados y talleres">
      <h3>Nivel académico</h3>
      <div className="check-row">
        {SCHOOL_LEVELS.map((level) => (
          <label key={level.id} className="check-chip">
            <input type="checkbox" checked={value.levels.includes(level.id)} onChange={(e) => setLevel(level.id, e.target.checked)} />
            {level.name}
          </label>
        ))}
      </div>
      {SCHOOL_LEVELS.filter((level) => value.levels.includes(level.id)).map((level) => {
        const grades = value.grades[level.id] ?? [];
        const workshops = value.workshops[level.id] ?? [];
        return (
          <fieldset key={level.id} className="school-level">
            <legend>
              {level.name} <small>Incluye {level.includedWorkshops} talleres</small>
            </legend>
            <div className="school-level__row">
              <span>{level.gradeUnit === "grado" ? "Grados" : "Semestres"}</span>
              <div className="check-row">
                {level.grades.map((grade) => (
                  <label key={grade} className="check-chip check-chip--small">
                    <input
                      type="checkbox"
                      aria-label={`${level.name} ${grade} ${level.gradeUnit}`}
                      checked={grades.includes(grade)}
                      onChange={(e) => setList("grades", level.id, grade, e.target.checked)}
                    />
                    {grade}
                  </label>
                ))}
              </div>
            </div>
            <div className="school-level__row">
              <span>Talleres</span>
              <div className="check-grid">
                {level.workshops.map((workshop) => (
                  <label key={workshop.id} className="check-card">
                    <input
                      type="checkbox"
                      aria-label={`${level.name}: ${workshop.name}`}
                      checked={workshops.includes(workshop.id)}
                      onChange={(e) => setList("workshops", level.id, workshop.id, e.target.checked)}
                    />
                    <span>
                      <strong>
                        {workshop.name}
                        {workshop.grades ? <em> · {workshop.grades}</em> : null}
                      </strong>
                      <small>{workshop.description}</small>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            {workshops.length > level.includedWorkshops && (
              <p className="school-level__note">
                Elegiste {workshops.length} talleres; el paquete de {level.name.toLowerCase()} incluye {level.includedWorkshops}.
              </p>
            )}
          </fieldset>
        );
      })}
      {/* The group besides the students (who are the first concept's quantity), side by side. */}
      <div className="school-group">
        {([
          ["teachers", "Profesores", "cortesía, sin costo"],
          ["companions", "Acompañantes", "padres u otros adultos"],
        ] as const).map(([key, label, note]) => (
          <label key={key} className="field">
            <span className="field__label">{label} <small>({note})</small></span>
            <span className="field__control">
              <input
                type="number"
                min={0}
                max={500}
                aria-label={label}
                value={value[key] ?? ""}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  onChange({ ...value, [key]: e.target.value === "" || !Number.isFinite(n) ? undefined : Math.max(0, Math.min(500, Math.round(n))) });
                }}
              />
            </span>
          </label>
        ))}
      </div>
      {value.levels.length > 0 && (
        <fieldset className="school-level">
          <legend>Menú elegido <small>Opcional: si el grupo ya lo decidió</small></legend>
          <div className="check-row" role="radiogroup" aria-label="Menú elegido">
            {[...schoolMenus(value.levels), ""].map((menu) => (
              <label key={menu || "none"} className="check-chip">
                <input type="radio" name="school-menu" checked={(value.menu ?? "") === menu} onChange={() => onChange({ ...value, menu: menu || undefined })} />
                {menu || "Por definir"}
              </label>
            ))}
          </div>
        </fieldset>
      )}
    </section>
  );
}
